import { Badge, Button, Drawer, Grid, List, Row, Typography, message } from "antd";
import { BellOutlined, ExclamationCircleTwoTone } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { httpRequest } from "../helpers/api";
import { getErrorMessage } from "../helpers/errorHandler";
import useAuth from "../hooks/useAuth";
import { messaging, onMessage } from "../firebase";
import { notificationError, notificationInfo, notificationSuccess } from "../screens/blog/posts/helpers/notification";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { theme } from "../assets/theme";
import { ReactComponent as CancelClosingIcon } from "../assets/Icons/CancelClosing.svg";
import { ReactComponent as CancelInvoiceIcon } from "../assets/Icons/CancelInvoice.svg";
import { ReactComponent as CancelReportIcon } from "../assets/Icons/CancelReport.svg";
import { ReactComponent as BirthdayIcon } from "../assets/Icons/BirthdayReminder.svg";
import { ReactComponent as NewCommentIcon } from "../assets/Icons/NewComment.svg";
import useFetchQuery from "../hooks/useFetchQuery";

export type NotificationProps = {
	id: number;
	externalId: string;
	senderUserId: string;
	receiverUserId: string;
	platform: string;
	title: string;
	message: string;
	body: any;
	status: string;
	readByUserAt: Date | null;
	requestData: {
		to: string;
		from: string;
		feature: string;
		templateId: string;
	};
	responseId: string;
	responseData: any;
	responseError: any;
	createdByUserId: string;
	metaCreatedByUser: any;
	metaSenderUser: any;
	metaReceiverUser: any;
	willBeDeletedAt: Date | null;
	createdAt: Date;
	updatedAt: Date;
};

export interface ListNotificationQuery {
	receiverUserId?: string;
	status?: string;
	isRead?: string;
}

export interface NotificationResponse {
	count: number;
	results: NotificationProps[];
	summary: {
		totalUnread: number;
	};
}

export default function AppNotification() {
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const { xl, md, lg } = Grid.useBreakpoint();
	const history = useHistory();

	const width = xl ? 40 : lg ? 50 : md ? 80 : 95;
	const { user } = useAuth();

	const { isLoading, data, fetchList } = useFetchQuery<NotificationResponse>({
		endpoint: "/my-notifications",
		isEnabled: !!user.userId,
	});
	const notifications = useMemo(() => {
		if (isLoading || !data) return [];
		return data.results;
	}, [isLoading, data]);
	const unreadCount = useMemo(() => {
		if (isLoading || !data) return 0;
		return data.summary?.totalUnread || 0;
	}, [isLoading, data]);

	const groupNotificationsByDate = useMemo(() => {
		return notifications.reduce((groups: any, notification) => {
			const date = moment(notification.createdAt).format("YYYY-MM-DD");
			if (!groups[date]) {
				groups[date] = [];
			}
			groups[date].push(notification);
			return groups;
		}, {});
	}, [notifications]);

	onMessage(messaging, (payload: any) => {
		console.log("payload message app notif::", payload);

		// setIsFetch(true);
		fetchList();

		let props = {
			message: payload?.notification?.title,
			description: payload?.notification?.body,
			duration: 5,
		};
		if (payload?.notification?.title.includes("Successfully")) {
			notificationSuccess(props);
		} else if (payload?.notification?.title.includes("Failed")) {
			notificationError(props);
		} else {
			notificationInfo(props);
		}
	});

	const doMarkAsRead = async (record: NotificationProps) => {
		try {
			await httpRequest.patch("/notifications/mark-as-read", {
				notificationIds: [record.id],
				receiverUserId: record.receiverUserId,
			});
			fetchList();
			// setIsFetch(true);
		} catch (error) {
			message.error(getErrorMessage(error));
		}
	};

	const doMarkAllAsRead = async () => {
		try {
			await httpRequest.patch("/notifications/mark-all-as-read");
			fetchList();
			// setIsFetch(true);
		} catch (error) {
			message.error(getErrorMessage(error));
		}
	};

	const handleClick = async (payload: NotificationProps) => {
		if (!payload.readByUserAt) {
			await doMarkAsRead(payload);
		}

		if (payload.requestData.feature === "dashboard" && payload.externalId === "birthday-reminder") {
			window.location.href = "/dashboard#birthday-list";
			setIsDrawerOpen(false);
		} else if (payload.requestData.feature === "masking") {
			history.push(`/masking/${payload.externalId}`);
			setIsDrawerOpen(false);
		} else if (payload.requestData.feature === "document") {
			history.push(`/document-center`);
			setIsDrawerOpen(false);
		} else if (payload.requestData.feature === "invoice-report") {
			history.push(`/invoice/${payload.externalId}`);
			setIsDrawerOpen(false);
		} else if (payload.requestData.feature === "request-board") {
			history.push(`/request-board?id=${payload.externalId}`);
			setIsDrawerOpen(false);
		} else if (payload.externalId) {
			let path = payload.requestData?.feature;

			if (payload.requestData?.feature === "blog") {
				path = "blog-posts";
			} else if (payload.requestData?.feature === "listings") {
				path = "listing";
			}

			setIsDrawerOpen(false);
			let url = `/${path}/${payload.externalId}`;

			history.push(url);
		}
	};

	const renderIcon = (type: string, title?: string) => {
		if (type === "invoice" && title === "Cancel Report HQ") {
			return <CancelReportIcon />;
		} else if (type === "invoice") {
			return <CancelInvoiceIcon />;
		} else if (type === "closing") {
			return <CancelClosingIcon />;
		} else if (type === "invoice-report") {
			return <CancelReportIcon />;
		} else if (type === "blog") {
			return <NewCommentIcon />;
		} else if (type === "birthday-reminder") {
			return <BirthdayIcon />;
		} else if (title === "Birthday Reminder") {
			return <BirthdayIcon />;
		} else {
			return <ExclamationCircleTwoTone />;
		}
	};

	return (
		<>
			<Drawer
				bodyStyle={{
					padding: 0,
				}}
				width={`${width}vw`}
				title={
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div>Notification</div>
						{unreadCount > 0 && (
							<Button
								type="link"
								style={{ fontSize: 12 }}
								onClick={doMarkAllAsRead}
							>
								Mark all as read
							</Button>
						)}
					</div>
				}
				onClose={() => setIsDrawerOpen(false)}
				open={isDrawerOpen}
			>
				<div
					id="scrollableDiv"
					style={{
						height: "85vh",
						overflow: "auto",
					}}
				>
					<div>
						{Object.entries(groupNotificationsByDate).map(([date, notifications]) => {
							const isToday = moment(date).isSame(moment(), "day");
							return (
								<div key={date}>
									<div
										style={{
											padding: "12px 16px",
											backgroundColor: theme.colors.primary,
										}}
									>
										<Typography.Text
											strong
											style={{
												fontWeight: 600,
												color: "#fff",
											}}
										>
											{isToday ? "Today" : moment(date).format("dddd, DD MMMM YYYY")}
										</Typography.Text>
									</div>
									<List
										style={{
											paddingLeft: 32,
											paddingTop: 6,
											paddingBottom: 6,
										}}
										loading={isLoading}
										dataSource={notifications as NotificationProps[]}
										renderItem={(item) => {
											return (
												<List.Item key={item.id}>
													<div
														onClick={() => handleClick(item)}
														style={{
															cursor: "pointer",
															width: "100%",
														}}
													>
														<List.Item.Meta
															avatar={renderIcon(item.requestData.feature, item.title)}
															title={
																<Row
																	justify="space-between"
																	style={{
																		marginBottom: 8,
																	}}
																>
																	<Badge
																		dot={item.readByUserAt ? false : true}
																		offset={[4, 0]}
																	>
																		{item.title}
																	</Badge>
																	<Typography.Text
																		type="secondary"
																		style={{
																			fontSize: 12,
																			paddingRight: 16,
																		}}
																	>
																		{/* {formatYearToTime(item.createdAt)} */}
																		{`${moment(item.createdAt).format(
																			"HH"
																		)}:${moment(item.createdAt).format("mm")}`}
																	</Typography.Text>
																</Row>
															}
															description={
																<div
																	style={{
																		width: "90%",
																	}}
																>
																	<Typography.Text type="secondary">
																		{item.message}
																	</Typography.Text>
																</div>
															}
														/>
													</div>
												</List.Item>
											);
										}}
									/>
								</div>
							);
						})}
					</div>
				</div>
			</Drawer>
			<a onClick={() => setIsDrawerOpen(true)}>
				<Badge count={unreadCount}>
					<BellOutlined style={{ fontSize: 20, color: "#fff" }} />
				</Badge>
			</a>
		</>
	);
}
